export interface RestreamerLoginCredentials {
    username: string;
    password: string;
}

export interface ProcessConfig {
    id?: string;
    type: 'ffmpeg' | '';
    reference?: string;
    input: ProcessIO[];
    output: ProcessIO[];
    options?: string[];
    autostart?: boolean;
    reconnect?: boolean;
    reconnect_delay_seconds?: number;
    stale_timeout_seconds?: number;
    limits?: ProcessLimits;
}

export interface ProcessIO {
    id?: string;
    address: string;
    options?: string[];
    cleanup?: ProcessCleanup[];
}

export interface ProcessCleanup {
    pattern: string;
    max_files?: number;
    max_file_age_seconds?: number;
    purge_on_delete?: boolean;
}

export interface ProcessLimits {
    cpu_usage?: number;
    memory_mbytes?: number;
    waitfor_seconds?: number;
}

export interface Process {
    id: string;
    type: string;
    reference?: string;
    created_at: number;
    updated_at: number;
    config: ProcessConfig;
    state?: ProcessState;
    report?: ProcessReport;
    metadata?: any;
}

export interface ProcessState {
    exec: string;
    order: string;
    command: string[];
    progress?: ProcessProgress;
    cpu_usage: number;
    memory_bytes: number;
    runtime_seconds: number;
    reconnect_seconds?: number;
    last_logline?: string;
}

export interface ProcessProgress {
    frame: number;
    packet: number;
    fps: number;
    q: number;
    size_kb: number;
    time: number;
    bitrate_kbit: number;
    speed: number;
    drop: number;
    dup: number;
    inputs?: ProcessProgressIO[];
    outputs?: ProcessProgressIO[];
}

export interface ProcessProgressIO {
    index: number;
    stream: number;
    id?: string;
    address?: string;
    type: string;
    codec: string;
    coder: string;
    format: string;
    frame: number;
    packet: number;
    fps: number;
    q: number;
    size_kb: number;
    bitrate_kbit: number;
    pps: number;
    keyframe: number;
    width?: number;
    height?: number;
    pix_fmt?: string;
    channels?: number;
    layout?: string;
    sampling_hz?: number;
}

export interface ProcessReport {
    created_at: number;
    prelude: string[];
    log: string[][];
    history?: ProcessReportHistoryEntry[];
}

export interface ProcessReportHistoryEntry {
    created_at: number;
    prelude: string[];
    log: string[][];
}

export interface ProcessCommand {
    command: 'start' | 'stop' | 'restart' | 'reload';
}

class RestreamerService {
    private baseUrl: string = '';
    private token: string | null = null;

    constructor() {
        if (typeof window !== 'undefined') {
            this.token = localStorage.getItem('restreamer_token');
        }
    }

    private getHeaders() {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        if (this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        }

        return headers;
    }

    async login(credentials: RestreamerLoginCredentials, apiUrl: string): Promise<void> {
        this.baseUrl = apiUrl;

        try {
            const response = await fetch(`${this.baseUrl}/api/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(credentials),
            });

            if (!response.ok) {
                throw new Error('Invalid credentials');
            }

            const data = await response.json();
            this.token = data.access_token;

            if (typeof window !== 'undefined') {
                localStorage.setItem('restreamer_token', this.token!);
                localStorage.setItem('restreamer_api_url', apiUrl);
            }
        } catch (error) {
            console.error('Login failed:', error);
            throw error;
        }
    }

    async logout(): Promise<void> {
        this.token = null;
        if (typeof window !== 'undefined') {
            localStorage.removeItem('restreamer_token');
            localStorage.removeItem('restreamer_api_url');
        }
    }

    async getProcesses(filters?: {
        filter?: string;
        reference?: string;
        id?: string;
        idpattern?: string;
        refpattern?: string;
    }): Promise<Process[]> {
        if (!this.baseUrl && typeof window !== 'undefined') {
            this.baseUrl = localStorage.getItem('restreamer_api_url') || '';
        }

        const params = new URLSearchParams();
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value) params.append(key, value);
            });
        }

        const url = `${this.baseUrl}/api/v3/process${params.toString() ? `?${params.toString()}` : ''}`;

        try {
            const response = await fetch(url, {
                headers: this.getHeaders(),
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch processes: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching processes:', error);
            throw error;
        }
    }

    async createProcess(config: ProcessConfig): Promise<ProcessConfig> {
        if (!this.baseUrl && typeof window !== 'undefined') {
            this.baseUrl = localStorage.getItem('restreamer_api_url') || '';
        }

        try {
            const response = await fetch(`${this.baseUrl}/api/v3/process`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify(config),
            });

            if (!response.ok) {
                throw new Error(`Failed to create process: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error creating process:', error);
            throw error;
        }
    }

    async updateProcess(id: string, config: ProcessConfig): Promise<ProcessConfig> {
        if (!this.baseUrl && typeof window !== 'undefined') {
            this.baseUrl = localStorage.getItem('restreamer_api_url') || '';
        }

        try {
            const response = await fetch(`${this.baseUrl}/api/v3/process/${id}`, {
                method: 'PUT',
                headers: this.getHeaders(),
                body: JSON.stringify(config),
            });

            if (!response.ok) {
                throw new Error(`Failed to update process: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error updating process:', error);
            throw error;
        }
    }

    async deleteProcess(id: string): Promise<void> {
        if (!this.baseUrl && typeof window !== 'undefined') {
            this.baseUrl = localStorage.getItem('restreamer_api_url') || '';
        }

        try {
            const response = await fetch(`${this.baseUrl}/api/v3/process/${id}`, {
                method: 'DELETE',
                headers: this.getHeaders(),
            });

            if (!response.ok) {
                throw new Error(`Failed to delete process: ${response.statusText}`);
            }
        } catch (error) {
            console.error('Error deleting process:', error);
            throw error;
        }
    }

    async sendCommand(id: string, command: string): Promise<void> {
        if (!this.baseUrl && typeof window !== 'undefined') {
            this.baseUrl = localStorage.getItem('restreamer_api_url') || '';
        }

        try {
            const response = await fetch(`${this.baseUrl}/api/v3/process/${id}/command`, {
                method: 'PUT',
                headers: this.getHeaders(),
                body: JSON.stringify({ command }),
            });

            if (!response.ok) {
                throw new Error(`Failed to send command: ${response.statusText}`);
            }
        } catch (error) {
            console.error('Error sending command:', error);
            throw error;
        }
    }

    async testConnection(apiUrl: string): Promise<boolean> {
        try {
            const response = await fetch(`${apiUrl}/api`, {
                method: 'GET',
            });

            return response.ok;
        } catch (error) {
            console.error('Connection test failed:', error);
            return false;
        }
    }

    async getReport(id: string): Promise<ProcessReport> {
        if (!this.baseUrl && typeof window !== 'undefined') {
            this.baseUrl = localStorage.getItem('restreamer_api_url') || '';
        }

        try {
            const response = await fetch(`${this.baseUrl}/api/v3/process/${id}/report`, {
                method: 'GET',
                headers: this.getHeaders(),
            });

            if (!response.ok) {
                throw new Error(`Failed to get report: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error getting report:', error);
            throw error;
        }
    }
}

export const restreamerService = new RestreamerService();
